const ignoreErrorsPatterns = [
  /(Hydration failed|error while hydrating)/,
  /Blocked a frame/,
];

/**
 * @param {import("@sentry/browser").Event} error
 */
const shouldIgnoreError = error =>
  (error.message &&
    ignoreErrorsPatterns.some(pattern => pattern.test(error.message))) ||
  error.status === 404 ||
  error.status === 410;

/**
 * @param {[import("@sentry/browser"), import("@sentry/tracing")]}
 */
const initSentry = ([sentry, sentryTracing]) => {
  sentry.init({
    dsn: window.SENTRY,
    release: window.RELEASE,
    tracesSampleRate: 0.001,
    beforeSend: event => {
      return shouldIgnoreError(event) ? null : event;
    },
    integrations: [
      new sentryTracing.BrowserTracing(),
      new sentry.Integrations.GlobalHandlers({
        onunhandledrejection: false,
      }),
    ],
    allowUrls: [
      /^https:\/\/pjrfe-75b8\.kxcdn\.com/,
      /^https:\/\/www\.prisjakt\.(nu|no)/,
      /^https:\/\/pricespy\.co\.(uk|nz)/,
      /^https:\/\/prisjagt\.dk/,
      /^https:\/\/hintaopas\.fi/,
      /^https:\/\/ledenicheur\.fr/,
    ],
  });
  return sentry;
};

const createRejectedSentryPromise = () => {
  const promise = new Promise((resolve, reject) =>
    // Make sure the promise is rejected after the .catch() handler is attached,
    // otherwise an unnecessary console error message will be logged
    requestAnimationFrame(reject)
  );
  promise.catch(() => {});
  return promise;
};

/**
 * @type {Promise<[import("@sentry/browser"), import("@sentry/tracing")]>}
 */
export const sentryPromise = window.SENTRY
  ? Promise.all([
      import(/* webpackChunkName: "sentry" */ '@sentry/browser'),
      import(
        /* webpackChunkName: "sentry-tracing" */ '@sentry/tracing/esm/browser'
      ),
    ]).then(initSentry)
  : createRejectedSentryPromise();

// Used by sagas
export const captureException = (...args) => {
  sentryPromise
    .then(([sentry]) => {
      sentry.captureException(...args);
    })
    .catch(() => {
      console.error(...args); // eslint-disable-line no-console
    });
};

// Used by sagas
export const captureMessage = (...args) => {
  sentryPromise
    .then(([sentry]) => {
      sentry.captureMessage(...args);
    })
    .catch(() => {
      console.error(...args); // eslint-disable-line no-console
    });
};
